import { useAuth } from "@clerk/clerk-react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
} from "contexts/OutreachContacts";
import { useContext, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  Campaign,
  CampaignStatus,
  TrackedCreatorWrapper,
} from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

type MenuView = "root" | "creator" | "campaign";

const DROPDOWN_PAGE_SIZE = 20;

// Draft campaigns have never sent, so they can't match any contacts.
const NON_DRAFT_CAMPAIGN_STATUSES = Object.values(CampaignStatus)
  .filter((status) => status !== CampaignStatus.draft)
  .map((status) => `statuses=${status}`)
  .join("&");

export default function InboxFilters() {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { fetchContactsParams, handleFetchContacts, currentSidebarOption } =
    useContext(OutreachContactsContext);
  const orgId = currentOrg?.id;

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [menuView, setMenuView] = useState<MenuView>("root");

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsTotal, setCampaignsTotal] = useState<number | null>(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const campaignPageRef = useRef(0);
  const campaignsLoadingRef = useRef(false);

  const [trackedCreators, setTrackedCreators] = useState<
    TrackedCreatorWrapper[]
  >([]);
  const [creatorsTotal, setCreatorsTotal] = useState<number | null>(null);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const creatorCursorRef = useRef<number | undefined>(undefined);
  const creatorsLoadingRef = useRef(false);

  const { ref: campaignSentinelRef, inView: campaignInView } = useInView();
  const { ref: creatorSentinelRef, inView: creatorInView } = useInView();

  // Campaigns paginate by page number.
  const loadCampaigns = async () => {
    if (!orgId || campaignsLoadingRef.current) return;
    campaignsLoadingRef.current = true;
    setCampaignsLoading(true);
    try {
      const page = campaignPageRef.current + 1;
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${orgId}/campaigns?per_page=${DROPDOWN_PAGE_SIZE}&page=${page}&${NON_DRAFT_CAMPAIGN_STATUSES}`,
      );
      campaignPageRef.current = page;
      setCampaignsTotal(res.count ?? null);
      setCampaigns((prev) => [...prev, ...(res.campaigns ?? [])]);
    } catch (error) {
    } finally {
      campaignsLoadingRef.current = false;
      setCampaignsLoading(false);
    }
  };

  // Tracked creators paginate by a created_at cursor (see TrackCreators list).
  const loadCreators = async () => {
    if (!orgId || creatorsLoadingRef.current) return;
    creatorsLoadingRef.current = true;
    setCreatorsLoading(true);
    try {
      let url = `/api/organization/${orgId}/tracked-creators?per_page=${DROPDOWN_PAGE_SIZE}`;
      if (creatorCursorRef.current) {
        url += `&cursor=${JSON.stringify([creatorCursorRef.current])}`;
      }
      const res = await fetcherAuth(getToken, url);
      const batch: TrackedCreatorWrapper[] = res.trackedCreators ?? [];
      setCreatorsTotal(res.total ?? null);
      const lastCreatedAt = batch[batch.length - 1]?.trackedCreator?.createdAt;
      if (lastCreatedAt) creatorCursorRef.current = lastCreatedAt;
      setTrackedCreators((prev) => [...prev, ...batch]);
    } catch (error) {
    } finally {
      creatorsLoadingRef.current = false;
      setCreatorsLoading(false);
    }
  };

  // Load the first page of each list up front so we know whether to surface
  // the filter entries; further pages stream in as the user scrolls.
  useEffect(() => {
    if (!orgId) return;
    campaignPageRef.current = 0;
    creatorCursorRef.current = undefined;
    setCampaigns([]);
    setTrackedCreators([]);
    setCampaignsTotal(null);
    setCreatorsTotal(null);
    loadCampaigns();
    loadCreators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const campaignsHasMore =
    campaignsTotal !== null && campaigns.length < campaignsTotal;
  const creatorsHasMore =
    creatorsTotal !== null && trackedCreators.length < creatorsTotal;

  useEffect(() => {
    if (
      menuView === "campaign" &&
      campaignInView &&
      campaignsHasMore &&
      !campaignsLoading
    ) {
      loadCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    menuView,
    campaignInView,
    campaignsHasMore,
    campaignsLoading,
    campaigns.length,
  ]);

  useEffect(() => {
    if (
      menuView === "creator" &&
      creatorInView &&
      creatorsHasMore &&
      !creatorsLoading
    ) {
      loadCreators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    menuView,
    creatorInView,
    creatorsHasMore,
    creatorsLoading,
    trackedCreators.length,
  ]);

  const campaignIds = fetchContactsParams.campaignIds ?? [];
  const creatorUsernames = fetchContactsParams.trackedCreatorUsernames ?? [];

  const update = (changes: Partial<typeof fetchContactsParams>) => {
    handleFetchContacts({ ...fetchContactsParams, page: 1, ...changes });
  };

  const closeAddMenu = () => {
    setAddAnchor(null);
    setMenuView("root");
  };

  // Single-value filters close the menu once chosen.
  const commit = (changes: Partial<typeof fetchContactsParams>) => {
    update(changes);
    closeAddMenu();
  };

  // Multi-select filters keep the menu open so several can be picked.
  const toggleCampaign = (id?: number) => {
    if (!id) return;
    const next = campaignIds.includes(id)
      ? campaignIds.filter((c) => c !== id)
      : [...campaignIds, id];
    update({ campaignIds: next.length ? next : undefined });
  };

  const toggleCreator = (username: string) => {
    const next = creatorUsernames.includes(username)
      ? creatorUsernames.filter((u) => u !== username)
      : [...creatorUsernames, username];
    update({
      trackedCreatorUsernames: next.length ? next : undefined,
      fromInstagramTracking: undefined,
    });
  };

  const clearAll = () => {
    update({
      importantOnly: false,
      fromVa: undefined,
      fromInstagramTracking: undefined,
      trackedCreatorUsernames: undefined,
      campaignIds: undefined,
    });
  };

  const canFilterNeedsResponse =
    currentSidebarOption?.key === InboxSidebarOptionKey.inbox;
  const canFilterVa = !!currentOrg?.isVaUser;
  const canFilterCreators = trackedCreators.length > 0;
  const canFilterCampaigns = campaigns.length > 0;

  if (
    !canFilterNeedsResponse &&
    !canFilterVa &&
    !canFilterCreators &&
    !canFilterCampaigns
  ) {
    return null;
  }

  const campaignName = (id: number) =>
    campaigns.find((c) => c.id === id)?.name ?? `Campaign ${id}`;

  const activeFilters: { key: string; label: string; onDelete: () => void }[] =
    [];
  if (fetchContactsParams.importantOnly) {
    activeFilters.push({
      key: "needs-response",
      label: "Needs response",
      onDelete: () => update({ importantOnly: false }),
    });
  }
  if (fetchContactsParams.fromVa) {
    activeFilters.push({
      key: "from-va",
      label: "From VA",
      onDelete: () => update({ fromVa: undefined }),
    });
  }
  if (fetchContactsParams.fromInstagramTracking) {
    activeFilters.push({
      key: "ig-any",
      label: "IG tagging: Any creator",
      onDelete: () => update({ fromInstagramTracking: undefined }),
    });
  }
  creatorUsernames.forEach((username) => {
    activeFilters.push({
      key: `creator-${username}`,
      label: `Creator: @${username}`,
      onDelete: () => toggleCreator(username),
    });
  });
  campaignIds.forEach((id) => {
    activeFilters.push({
      key: `campaign-${id}`,
      label: `Campaign: ${campaignName(id)}`,
      onDelete: () => toggleCampaign(id),
    });
  });

  const hasAddable =
    (canFilterNeedsResponse && !fetchContactsParams.importantOnly) ||
    (canFilterVa && !fetchContactsParams.fromVa) ||
    canFilterCreators ||
    canFilterCampaigns;

  const backItem = (
    <MenuItem key="back" onClick={() => setMenuView("root")}>
      <Box
        component="i"
        className="fa-regular fa-chevron-left"
        sx={{ mr: 1, fontSize: 12 }}
      />
      Back
    </MenuItem>
  );

  const loadingRow = (
    key: string,
    sentinelRef: (node?: Element | null) => void,
    loading: boolean,
  ) => (
    <Box
      key={key}
      ref={sentinelRef}
      sx={{ display: "flex", justifyContent: "center", py: 1 }}
    >
      {loading && <CircularProgress size={18} />}
    </Box>
  );

  return (
    <>
      <Tooltip title="Filters">
        <Badge
          badgeContent={activeFilters.length}
          color="primary"
          overlap="circular"
        >
          <IconButton
            size="small"
            onClick={(e) => setPopoverAnchor(e.currentTarget)}
            sx={{
              color: activeFilters.length ? "primary.main" : "text.secondary",
            }}
          >
            <Box
              component="i"
              className="fa-solid fa-bars-filter"
              sx={{ fontSize: 16 }}
            />
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 2,
            width: 300,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2">Filters</Typography>
            {activeFilters.length > 0 && (
              <Button
                size="small"
                onClick={clearAll}
                sx={{ textTransform: "none", minWidth: 0 }}
              >
                Clear all
              </Button>
            )}
          </Box>

          {activeFilters.length > 0 ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Showing contacts that match all of these:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {activeFilters.map((filter) => (
                  <Chip
                    key={filter.key}
                    size="small"
                    label={filter.label}
                    onDelete={filter.onDelete}
                  />
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No filters added yet.
            </Typography>
          )}

          {hasAddable && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                setMenuView("root");
                setAddAnchor(e.currentTarget);
              }}
              startIcon={<Box component="i" className="fa-regular fa-plus" />}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                color: "text.primary",
                borderColor: "grey.300",
              }}
            >
              Add filter
            </Button>
          )}
        </Box>
      </Popover>

      <Menu
        anchorEl={addAnchor}
        open={!!addAnchor}
        onClose={closeAddMenu}
        disableAutoFocusItem
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 280, maxHeight: 380 } } }}
      >
        {menuView === "root" && [
          canFilterNeedsResponse && !fetchContactsParams.importantOnly && (
            <MenuItem
              key="needs-response"
              onClick={() => commit({ importantOnly: true })}
            >
              Needs response
            </MenuItem>
          ),
          canFilterVa && !fetchContactsParams.fromVa && (
            <MenuItem key="from-va" onClick={() => commit({ fromVa: true })}>
              From VA
            </MenuItem>
          ),
          canFilterCreators && (
            <MenuItem key="creator" onClick={() => setMenuView("creator")}>
              Tracked creators
              <Box
                component="i"
                className="fa-regular fa-chevron-right"
                sx={{ ml: "auto", pl: 2, fontSize: 12 }}
              />
            </MenuItem>
          ),
          canFilterCampaigns && (
            <MenuItem key="campaign" onClick={() => setMenuView("campaign")}>
              Campaigns
              <Box
                component="i"
                className="fa-regular fa-chevron-right"
                sx={{ ml: "auto", pl: 2, fontSize: 12 }}
              />
            </MenuItem>
          ),
        ]}

        {menuView === "creator" && [
          backItem,
          <MenuItem
            key="any"
            onClick={() =>
              update({
                fromInstagramTracking: fetchContactsParams.fromInstagramTracking
                  ? undefined
                  : true,
                trackedCreatorUsernames: undefined,
              })
            }
          >
            <Checkbox
              size="small"
              checked={!!fetchContactsParams.fromInstagramTracking}
              sx={{ p: 0, mr: 1 }}
            />
            Any IG-tagged brand
          </MenuItem>,
          <Divider key="divider" />,
          ...trackedCreators.map((creator) => (
            <MenuItem
              key={creator.sourceInstagramUsername}
              onClick={() => toggleCreator(creator.sourceInstagramUsername)}
            >
              <Checkbox
                size="small"
                checked={creatorUsernames.includes(
                  creator.sourceInstagramUsername,
                )}
                sx={{ p: 0, mr: 1 }}
              />
              @{creator.sourceInstagramUsername}
            </MenuItem>
          )),
          (creatorsHasMore || creatorsLoading) &&
            loadingRow("creator-sentinel", creatorSentinelRef, creatorsLoading),
        ]}

        {menuView === "campaign" && [
          backItem,
          ...campaigns.map((campaign) => (
            <MenuItem
              key={campaign.id}
              onClick={() => toggleCampaign(campaign.id)}
            >
              <Checkbox
                size="small"
                checked={!!campaign.id && campaignIds.includes(campaign.id)}
                sx={{ p: 0, mr: 1 }}
              />
              <Tooltip title={campaign.name}>
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {campaign.name}
                </Typography>
              </Tooltip>
            </MenuItem>
          )),
          (campaignsHasMore || campaignsLoading) &&
            loadingRow(
              "campaign-sentinel",
              campaignSentinelRef,
              campaignsLoading,
            ),
        ]}
      </Menu>
    </>
  );
}
